import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function TemplateSyncButton({ projectId, onDone }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get(`/projects/${projectId}/template-status`).then((r) => setStatus(r.data)).catch(() => setStatus(null)); }, [projectId]);
  if (!status?.eligible) return null;
  return (
    <>
      <Button type="button" variant="outline" className="w-full" onClick={() => setOpen(true)} data-testid="template-sync-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Update from Pathway Template</Button>
      {open && <TemplateSyncDialog projectId={projectId} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone(); }} />}
    </>
  );
}

function Line({ n, children, testId }) {
  return <li className="flex gap-2 text-sm" data-testid={testId}><span className="w-8 text-right font-semibold text-slate-900">{n}</span><span className="text-slate-600">{children}</span></li>;
}

function TemplateSyncDialog({ projectId, onClose, onDone }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/projects/${projectId}/template-sync`).then((r) => setS(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  const apply = async () => {
    if (s.destructive && !window.confirm("This update may replace existing Project data. Continue anyway?")) return;
    setSaving(true);
    try { await api.post(`/projects/${projectId}/template-sync`); toast.success("Project updated from pathway template"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="template-sync-dialog">
        <DialogHeader><DialogTitle>Update from {s?.category_name || "Pathway"} Template</DialogTitle>
          <DialogDescription>Adds what's new in the template. Nothing in this project is deleted.</DialogDescription></DialogHeader>
        {!s ? <p className="text-sm text-slate-400">Comparing…</p> : (
          <div className="space-y-3">
            <div className="text-sm"><p className="text-slate-500">Current Project: <span className="font-medium text-slate-800">{s.project_name}</span></p>
              <p className="text-slate-500">Template: <span className="font-medium text-slate-800">{s.template_name}</span></p></div>
            {s.destructive && <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="template-sync-warning"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />Warning — Existing Project Data may be replaced. Please review the Project before continuing.</div>}
            <ul className="space-y-1" data-testid="template-sync-summary">
              <Line n={s.new_milestones} testId="ts-new-milestones">new Milestone(s) will be added{s.new_milestone_titles.length ? `: ${s.new_milestone_titles.join(", ")}` : ""}</Line>
              <Line n={s.new_tasks} testId="ts-new-tasks">new Task(s) will be added{s.new_task_titles.length ? `: ${s.new_task_titles.slice(0, 5).join(", ")}${s.new_task_titles.length > 5 ? "…" : ""}` : ""}</Line>
              <Line n={s.task_descriptions_updated + s.milestone_descriptions_updated} testId="ts-desc">description(s) will be updated</Line>
              <Line n={s.milestone_positions_updated} testId="ts-positions">Milestone position(s) will be updated</Line>
              <Line n={s.task_milestone_links_updated} testId="ts-links">Task(s) will move to their template Milestone</Line>
              <Line n={s.existing_tasks_preserved} testId="ts-preserved">existing Task(s) will be preserved</Line>
              <Line n={s.manual_tasks_unchanged + s.manual_milestones_unchanged} testId="ts-manual">manually added Task(s)/Milestone(s) will remain unchanged</Line>
              <Line n={s.tasks_deleted + s.milestones_deleted} testId="ts-deleted">Tasks or Milestones will be deleted</Line>
            </ul>
            {s.no_longer_in_template.length > 0 && <p className="text-xs text-slate-500" data-testid="ts-gone">No longer in current template (kept as is): {s.no_longer_in_template.map((x) => x.title).join(", ")}</p>}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="template-sync-cancel">Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" disabled={!s || saving} onClick={apply} data-testid="template-sync-apply">{saving ? "Updating…" : s?.destructive ? "Continue Anyway" : "Update Project"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
